import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CLICK_ACTION, SAVE_PAY_FREQUENCY } from '../../constants/tagManager';
import TagManagerService from '../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../translations/prospects';
import { formatDateISO } from '../../utils/dates/dates';
import CustomButton from '../Buttons/CustomButton/CustomButton';
import CustomInput from '../Inputs/CustomInput/CustomInput';
import CustomSelect from '../Inputs/CustomSelect/CustomSelect';
import SetupSection from '../SetupSection/SetupSection';

import './PayrollSection.scss';

const SETUP = TRANSLATIONS_PROSPECTS.profile.activationSetup;

export interface IPayrollSchedule {
  frequency: string;
  lastPaymentDate: string;
}

interface IPayrollSectionProps {
  quoteId: number;
  saved: IPayrollSchedule;
  onSave: (schedule: IPayrollSchedule) => Promise<void>;
}

const PayrollSection = ({ quoteId, saved, onSave }: IPayrollSectionProps) => {
  const { t } = useTranslation();

  const [frequency, setFrequency] = useState(saved.frequency);
  const [lastPaymentDate, setLastPaymentDate] = useState(saved.lastPaymentDate);
  const [saving, setSaving] = useState(false);

  const [prevQuoteId, setPrevQuoteId] = useState(quoteId);
  if (quoteId !== prevQuoteId) {
    setPrevQuoteId(quoteId);
    setFrequency(saved.frequency);
    setLastPaymentDate(saved.lastPaymentDate);
    setSaving(false);
  }

  const dirty = frequency !== saved.frequency || lastPaymentDate !== saved.lastPaymentDate;
  const isDone = Boolean(saved.frequency && saved.lastPaymentDate);
  const hasSaved = Boolean(saved.frequency || saved.lastPaymentDate);

  const frequencyOptions = useMemo(
    () => [
      { value: 'weekly', label: t(SETUP.payroll.frequency.weekly) },
      { value: 'biweekly', label: t(SETUP.payroll.frequency.biweekly) },
      { value: 'semi_monthly', label: t(SETUP.payroll.frequency.semiMonthly) },
      { value: 'monthly', label: t(SETUP.payroll.frequency.monthly) }
    ],
    [t]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ frequency, lastPaymentDate });
      TagManagerService.pushEvent(CLICK_ACTION, SAVE_PAY_FREQUENCY, { frequency });
    } catch {
      // no-op: keep draft so the broker can retry
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFrequency(saved.frequency);
    setLastPaymentDate(saved.lastPaymentDate);
  };

  const today = formatDateISO(new Date());

  const handleLastPaymentDateChange = (value: string) => {
    if (value > today) return;
    setLastPaymentDate(value);
  };

  return (
    <SetupSection title={t(SETUP.payroll.title)} done={isDone}>
      <p className="payroll-section__intro">{t(SETUP.payroll.intro)}</p>
      <div className="payroll-section__row">
        <CustomSelect
          label={t(SETUP.payroll.frequencyLabel)}
          placeholder={t(SETUP.payroll.frequencyPlaceholder)}
          options={frequencyOptions}
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          containerClassName="payroll-section__field"
        />
        <CustomInput
          label={t(SETUP.payroll.lastPaymentLabel)}
          type="date"
          value={lastPaymentDate}
          max={today}
          onChange={(e) => handleLastPaymentDateChange(e.target.value)}
          containerClassName="payroll-section__field"
        />
      </div>

      {(dirty || hasSaved) && (
        <div className="payroll-section__actions">
          {dirty ? (
            <>
              <span className="payroll-section__status" role="status" aria-live="polite">
                {t(SETUP.payroll.unsavedChanges)}
              </span>
              {!saving && (
                <CustomButton variant="default" size="s" onClick={handleCancel}>
                  {t(SETUP.payroll.cancel)}
                </CustomButton>
              )}
              <CustomButton variant="primary" size="s" loading={saving} onClick={handleSave}>
                {t(SETUP.payroll.save)}
              </CustomButton>
            </>
          ) : (
            <span className="payroll-section__saved" role="status" aria-live="polite">
              {t(SETUP.payroll.saved)}
            </span>
          )}
        </div>
      )}
    </SetupSection>
  );
};

export default PayrollSection;
